"use client";

// FILE: src/features/seo-portal/components/review-histogram.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: Горизонтальный stacked bar распределения отзывов по звёздам (★5…★1) для карточки ЧОП.
// SCOPE: Только отображение. Не загружает данные — принимает готовый список reviewsList.
// INPUT: reviewsList: { rating: number }[] (rating 1-5).
// OUTPUT: React-разметка (stacked bar + legend 5 строк с цветным квадратом, количеством, процентом).
// KEYWORDS: DOMAIN(SEO): ReviewHistogram; DOMAIN(Visualization): StackedBarChart; TECH(React): ClientComponent
// END_MODULE_CONTRACT
//
// START_RATIONALE:
// Q: Почему цвета от зелёного к красному (5→1), а не наоборот?
// A: Визуальная иерархия: хорошие оценки слева (доминантный зелёный), плохие справа (красный).
//    Соответствует спектральной шкале «отлично → ужасно».
// Q: Почему stacked bar, а не отдельные строки как в reviews-section.tsx?
// A: Stacked bar даёт наглядное представление доли каждой оценки в общем объёме за один взгляд.
//    Отдельные строки + легенда обеспечивают точные цифры.
// Q: Почему проценты считаются от reviewsList.length, а не от чего-то ещё?
// A: Каждый отзыв имеет ровно одну оценку 1-5, поэтому сумма всех count = reviewsList.length.
//    Процент = count / total * 100 всегда даёт 100% заполнение бара.
// END_RATIONALE
//
// START_CHANGE_SUMMARY:
// LAST_CHANGE: 1.0.0 — Создан компонент. Stacked bar (5 сегментов), legend (5 строк), placeholder "Нет отзывов".
// END_CHANGE_SUMMARY
//
// START_MODULE_MAP:
// COMP [8] Горизонтальный stacked bar распределения отзывов по звёздам => ReviewHistogram
// TYPE [3] Пропсы компонента => ReviewHistogramProps
// TYPE [4] Элемент распределения одной звезды => StarDistribution
// CONST [5] Палитра цветов звёзд (5→1) => STAR_COLORS
// FUNC [4] Расчёт массива распределения => buildDistribution
// END_MODULE_MAP

import { Star } from "lucide-react";

// START_INTERFACE: ReviewHistogramProps
interface ReviewHistogramProps {
  /** Список отзывов с рейтингом 1-5 */
  reviewsList: { rating: number }[];
}
// END_INTERFACE

// START_CONST: STAR_COLORS
/**
 * Палитра цветов для звёзд 5→1.
 * Зелёный спектр для высоких оценок, красный для низких.
 */
const STAR_COLORS: Record<number, string> = {
  5: "#22c55e", // зеленый — отлично
  4: "#84cc16", // лайм — хорошо
  3: "#eab308", // жёлтый — средне
  2: "#f97316", // оранжевый — плохо
  1: "#ef4444", // красный — ужасно
} as const;
// END_CONST

// START_TYPE: StarDistribution
interface StarDistribution {
  star: number;
  count: number;
  percentage: number;
}
// END_TYPE

// START_FUNCTION: buildDistribution
/**
 * Строит массив распределения по звёздам (5→1) с количеством и процентом.
 * Процент считается от общего числа отзывов.
 * При пустом списке возвращает нулевые значения для всех 5 звёзд.
 */
function buildDistribution(
  reviewsList: { rating: number }[]
): StarDistribution[] {
  const total = reviewsList.length;
  const safeTotal = total > 0 ? total : 1;

  return [5, 4, 3, 2, 1].map((star) => {
    const count = reviewsList.filter((r) => r.rating === star).length;
    return {
      star,
      count,
      percentage: (count / safeTotal) * 100,
    };
  });
}
// END_FUNCTION

/**
 * ReviewHistogram — горизонтальный stacked bar распределения отзывов по звёздам.
 *
 * Состоит из 2 блоков:
 * 1. Stacked bar — горизонтальная полоса из 5 цветных сегментов (★5…★1)
 * 2. Legend — 5 строк с цветным квадратиком, ★, количеством, процентом
 *
 * При пустом списке отзывов отображает placeholder "Нет отзывов".
 */
export function ReviewHistogram({ reviewsList }: ReviewHistogramProps) {
  const distribution = buildDistribution(reviewsList);

  // START_BLOCK_EMPTY: Плейсхолдер при отсутствии отзывов
  if (reviewsList.length === 0) {
    return (
      <div className="bg-white/5 border border-dashed border-white/10 p-6">
        <div className="text-[10px] uppercase tracking-[0.1em] text-white/40 font-mono text-center">
          Нет отзывов
        </div>
      </div>
    );
  }
  // END_BLOCK_EMPTY

  return (
    <div className="border border-white/10 bg-white/5 p-5 space-y-4">
      {/* === 1. Stacked bar === */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.1em] text-white/50 mb-3 font-mono">
          Распределение отзывов
        </div>
        <div
          className="flex h-8 border border-white/20 overflow-hidden bg-black"
          role="img"
          aria-label="Распределение отзывов по звёздам"
        >
          {distribution.map((item) => (
            <div
              key={item.star}
              style={{
                backgroundColor: STAR_COLORS[item.star],
                width: `${item.percentage}%`,
              }}
              className="h-full transition-all duration-500 min-w-0"
              title={`★${item.star}: ${item.count} (${item.percentage.toFixed(1)}%)`}
            />
          ))}
        </div>
      </div>

      {/* === 2. Legend — 5 строк с цветным квадратиком, звездой, количеством, процентом === */}
      <div className="space-y-1.5">
        {distribution.map((item) => (
          <div
            key={item.star}
            className="flex items-center gap-2 text-[13px] text-white/60"
          >
            <span
              className="w-3 h-3 flex-none border border-white/20"
              style={{ backgroundColor: STAR_COLORS[item.star] }}
            />
            <span className="flex items-center gap-1 w-16">
              <Star className="w-3 h-3 fill-current text-white/40" />
              {item.star}
            </span>
            <span className="ml-auto font-mono text-white/60">
              {item.count}
            </span>
            <span className="font-mono text-[11px] text-white/40 w-10 text-right">
              {item.percentage.toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
